import * as z from 'zod';
import { dateOnlyOf, orgCalendarDate, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { GENDER_OPTIONS } from '@/constant/hr/genders';
import { toDisplayName } from './display-name';
import { Employee, EmployeeInsertInput, Sex } from '../types/employee.types';

const requiredMessage = { message: 'ERR_MSG_REQUIRED' };

/** A birth date is a picked day ("YYYY-MM-DD"), never after the organization's today. */
const birthDate = z
  .string()
  .refine((day) => !day || day <= orgCalendarDate(new Date().toISOString()), {
    message: 'DATE_OF_BIRTH_IN_FUTURE',
  });

/**
 * Legacy rules (`accountInfoForm`): salutation, first / last name, gender, joining date, country of employment,
 * unit and designation are required; everything else (including the employee
 * id, which the backend can generate, and the reporting manager) is optional. Every field is stored on the
 * `UserProfileDetail`; the personal, academic and previous-employment details live on the `UserContactDetail`
 * and are edited on the contact tab (`contact-info-schema`).
 */
export const basicInfoSchema = z.object({
  ProfileImageId: z.string(),

  Salutation: z.string().min(1, requiredMessage),
  FirstName: z.string().trim().min(1, requiredMessage),
  LastName: z.string().trim().min(1, requiredMessage),
  Sex: z.string().min(1, requiredMessage),
  DateOfBirth: birthDate,
  DateOfBirthOriginal: birthDate,

  JoiningDate: z.string().min(1, requiredMessage),
  DepartmentId: z.string().min(1, requiredMessage),
  DepartmentName: z.string(),
  DepartmentKey: z.string(),
  ReportingManagerUserId: z.string(),
  ReportingManagerName: z.string(),
  DesignationId: z.string().min(1, requiredMessage),
  Designation: z.string(),
  EmployeeID: z.string(),
  CtcLevelId: z.string(),
});

export type BasicInfoValues = z.infer<typeof basicInfoSchema>;

export const basicInfoDefaults: BasicInfoValues = {
  ProfileImageId: '',

  Salutation: '',
  FirstName: '',
  LastName: '',
  Sex: '',
  DateOfBirth: '',
  DateOfBirthOriginal: '',

  JoiningDate: '',
  DepartmentId: '',
  DepartmentName: '',
  DepartmentKey: '',
  ReportingManagerUserId: '',
  ReportingManagerName: '',
  DesignationId: '',
  Designation: '',
  EmployeeID: '',
  CtcLevelId: '',
};

const isSex = (value: string): value is Sex =>
  GENDER_OPTIONS.some((option) => option.value === value);

export const basicInfoFromEmployee = (employee: Employee): Partial<BasicInfoValues> => ({
  ProfileImageId: employee.ProfileImageId ?? '',

  Salutation: employee.Salutation ?? '',
  FirstName: employee.FirstName ?? '',
  LastName: employee.LastName ?? '',
  Sex: employee.Sex ?? '',
  DateOfBirth: dateOnlyOf(employee.DateOfBirth),
  DateOfBirthOriginal: dateOnlyOf(employee.DateOfBirthOriginal),

  JoiningDate: dateOnlyOf(employee.JoiningDate),
  DepartmentId: employee.DepartmentId ?? '',
  DepartmentName: employee.DepartmentName ?? '',
  DepartmentKey: employee.DepartmentKey ?? '',
  ReportingManagerUserId: employee.ReportingManagerUserId ?? '',
  ReportingManagerName: employee.ReportingManagerName ?? '',
  DesignationId: employee.DesignationId ?? '',
  Designation: employee.Designation ?? '',
  EmployeeID: employee.EmployeeID ?? '',
  CtcLevelId: employee.CtcLevelId ?? '',
});

/** Optional fields the person may empty again; `null` clears them, since an omitted field is left as stored. */
type ClearableField = 'DateOfBirth' | 'DateOfBirthOriginal' | 'CtcLevelId';

/**
 * `EmployeeInsertInput` is a shared type this step may not edit, yet the legacy payload also carries the profile
 * photo and the denormalised names the directory list / details read (all real `Employee` fields).
 */
export type BasicInfoPayload = Omit<Partial<EmployeeInsertInput>, ClearableField> & {
  [K in ClearableField]?: string | null;
} & {
  ProfileImageId?: string;
  DepartmentName?: string;
  DepartmentKey?: string;
  Designation?: string;
  ReportingManagerName?: string;
};

export const basicInfoToPayload = (values: BasicInfoValues): BasicInfoPayload => ({
  ProfileImageId: values.ProfileImageId || undefined,

  Salutation: values.Salutation,
  FirstName: values.FirstName.trim(),
  LastName: values.LastName.trim(),
  DisplayName: toDisplayName(values.FirstName, values.LastName),
  Sex: isSex(values.Sex) ? values.Sex : undefined,
  DateOfBirth: toDateOnlyValue(values.DateOfBirth) ?? null,
  DateOfBirthOriginal: toDateOnlyValue(values.DateOfBirthOriginal) ?? null,

  JoiningDate: toDateOnlyValue(values.JoiningDate),
  DepartmentId: values.DepartmentId,
  DepartmentName: values.DepartmentName,
  DepartmentKey: values.DepartmentKey || undefined,
  ReportingManagerUserId: values.ReportingManagerUserId,
  ReportingManagerName: values.ReportingManagerName,
  DesignationId: values.DesignationId,
  Designation: values.Designation,
  // Missing on a self-service save: that schema omits the HR-owned fields, and zod strips them.
  EmployeeID: (values.EmployeeID ?? '').trim(),
  CtcLevelId: values.CtcLevelId || null,
});
